import React from 'react';
import ProjectLayout from '../components/ProjectLayout';
import VulnerabilityScannerDemo from '../components/demos/VulnerabilityScannerDemo';

const VulnerabilityScannerDemoPage = ({ setCurrentPage }) => {
  return (
    <ProjectLayout
      title="Vulnerability Scanner"
      subtitle="Automated scanning, risk assessment, reporting, and remediation guides"
      emoji="🔍"
      onBack={() => setCurrentPage('work')}
      next={{ label: 'Penetration Testing', onClick: () => setCurrentPage('penetration-testing-demo') }}
      demo={<VulnerabilityScannerDemo />}
      overview="Automated vulnerability scanning system that identifies security weaknesses in web applications and infrastructure. The scanner detects SQL injection, XSS, authentication issues, missing security headers, and checks against CVE databases for known vulnerabilities."
      role="Security engineering, vulnerability assessment, penetration testing, automation, and reporting"
      stack={["Python", "Requests", "Nmap", "CVE Database", "SQLMap", "OWASP ZAP"]}
      challenges={[
        "Implementing comprehensive vulnerability detection algorithms",
        "Integrating with CVE databases for known vulnerabilities",
        "Generating actionable remediation reports",
        "Handling false positives and tuning detection rules",
        "Ensuring ethical scanning practices"
      ]}
      results={[
        "Identified 100+ vulnerabilities across multiple applications",
        "Reduced false positive rate to <5%",
        "Generated comprehensive remediation reports",
        "Integrated with CI/CD pipelines for continuous scanning",
        "Achieved 95% vulnerability detection accuracy"
      ]}
      problem="Applications and infrastructure need regular security assessments to identify vulnerabilities. Manual security testing is time-consuming and may miss issues. Automated vulnerability scanning provides continuous security assessment."
      approach="Built an automated vulnerability scanner that performs comprehensive security assessments including SQL injection, XSS, authentication issues, and security header analysis. The scanner integrates with CVE databases and generates detailed remediation reports."
      highlights={[
        "Automated vulnerability detection",
        "SQL injection and XSS scanning",
        "Security header analysis",
        "CVE database integration",
        "Risk assessment and prioritization",
        "Comprehensive remediation reports"
      ]}
      tutorialSummary="Build an automated vulnerability scanner that identifies security weaknesses in web applications. Learn vulnerability detection techniques, CVE database integration, and how to generate actionable security reports."
      difficulty="Advanced"
      timeEstimate="120 min"
      keyConcepts={[
        { name: "Vulnerability Assessment", description: "Identifying security weaknesses" },
        { name: "Penetration Testing", description: "Testing security defenses" },
        { name: "CVE Database", description: "Common Vulnerabilities and Exposures" },
        { name: "Security Headers", description: "HTTP security headers" }
      ]}
      tutorialSteps={[
        {
          title: "Setup Scanner Environment",
          description: "Install dependencies and configure scanner",
          steps: [
            "Install Python and required libraries",
            "Set up CVE database access",
            "Configure scanner settings",
            "Set up target whitelist/blacklist"
          ]
        },
        {
          title: "Implement Vulnerability Detection",
          description: "Build detection algorithms for common vulnerabilities",
          steps: [
            "Implement SQL injection detection",
            "Create XSS vulnerability scanner",
            "Build authentication vulnerability checks",
            "Add security header analysis"
          ]
        },
        {
          title: "Integrate CVE Database",
          description: "Connect to CVE databases for known vulnerabilities",
          steps: [
            "Set up CVE database API access",
            "Implement version detection",
            "Query CVE database for vulnerabilities",
            "Parse and store CVE information"
          ]
        },
        {
          title: "Generate Reports",
          description: "Create comprehensive vulnerability reports",
          steps: [
            "Format scan results",
            "Generate risk assessments",
            "Create remediation guides",
            "Export reports in multiple formats"
          ]
        }
      ]}
      setupInstructions={`1. Install dependencies:
pip install requests beautifulsoup4 python-nmap

2. Set up CVE database:
Configure access to NVD API or CVE database

3. Configure scanner:
Set target URLs and scanning parameters

4. Run scanner:
python scanner.py --target https://example.com`}
      deploymentGuide={`1. Set up scanner server:
Deploy scanner to secure server

2. Configure scheduling:
Set up cron jobs or scheduled tasks

3. Set up reporting:
Configure email/Slack notifications

4. Integrate with CI/CD:
Add scanner to CI/CD pipeline`}
      troubleshooting={[
        {
          issue: "High false positive rate",
          solution: "Tune detection rules and add whitelist for known false positives"
        },
        {
          issue: "Scanner blocked by target",
          solution: "Configure proper User-Agent headers and rate limiting"
        },
        {
          issue: "CVE database connection issues",
          solution: "Implement retry logic and caching for CVE lookups"
        }
      ]}
    />
  );
};

export default VulnerabilityScannerDemoPage;

